<template>
  <div v-if="hasInlineActions" class="inline-actions" @click.stop>
    <Button icon="pi pi-ellipsis-v" severity="secondary" size="small" text rounded @click="toggleMenu"
      class="inline-actions-trigger" />

    <Menu ref="menu" :model="menuItems" :popup="true" class="inline-actions-menu" />
  </div>
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { useAppStore } from '@/stores/app'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { storeToRefs } from 'pinia'
import { computed, inject, ref } from 'vue'

interface Props {
  elementId: string
  panelId: string
}

const props = defineProps<Props>()

// Use composable instead of inject
const { pageActions } = usePageData()
const onAction = inject<((context: any) => void)>('onAction', () => { })

const menu = ref()
const appStore = useAppStore()
const { hasActionRoleAccess } = useActionRoleAccess()
const { currentPlatform } = storeToRefs(appStore)

const hasRequiredRole = (action: any): boolean => hasActionRoleAccess(action.roles)

// Get inline actions for this element
const inlineActions = computed(() => {
  const filtered = pageActions.value.filter(action => {
    const levelMatch = action.layout?.level?._text === 'element'
    const idMatch = action.layout?.id?._text === props.elementId
    const positionMatch = action.layout?.position?._text === 'inline'
    const roleMatch = hasRequiredRole(action)

    return levelMatch && idMatch && positionMatch && roleMatch
  }).sort((a, b) => {
    const sortA = parseInt(a._attributes?.sort) || 0
    const sortB = parseInt(b._attributes?.sort) || 0
    return sortA - sortB
  })

  return filtered
})

const hasInlineActions = computed(() => inlineActions.value.length > 0)

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

// Group inline actions by dropDownActionName
const menuItems = computed(() => {
  const groups: Map<string, any[]> = new Map()
  const ungrouped: any[] = []

  inlineActions.value.forEach(action => {
    const dropDownName = getDropDownName(action)
    if (dropDownName !== '') {
      if (!groups.has(dropDownName)) {
        groups.set(dropDownName, [])
      }
      groups.get(dropDownName)!.push(action)
    } else {
      ungrouped.push(action)
    }
  })

  const result: any[] = []

  // Add ungrouped actions
  ungrouped.forEach(action => {
    result.push({
      label: action.title?._text || action.name?._text || 'Action',
      icon: action.image?._text,
      command: () => executeAction(action)
    })
  })

  // Add grouped actions as nested menus
  groups.forEach((actions, groupName) => {
    const mainAction = actions.find(a => isDropDownMain(a)) || actions[0]

    result.push({
      label: mainAction.title?._text || mainAction.name?._text || groupName,
      icon: mainAction.image?._text,
      items: actions.map(action => ({
        label: action.title?._text || action.name?._text || 'Action',
        icon: action.image?._text,
        command: () => executeAction(action)
      }))
    })
  })

  return result
})


const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

const executeAction = async (action: any) => {
  const platform = action.platform?._text || currentPlatform.value || undefined

  if (platform) {
    appStore.updateCurrentPlatform(platform)
  }

  const ctx = {
    id: action._attributes ? +action._attributes.id : 0,
    name: action.name?._text || '',
    elementId: props.elementId,
    panelId: props.panelId,
    level: 'element',
    data: action,
    platform
  }

  await onAction({ getContext: () => ctx })
}
</script>

<style scoped lang="scss">
.inline-actions {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.inline-actions-trigger {
  min-width: 24px !important;
  height: 24px !important;
  padding: 4px !important;

  :deep(.p-button-icon) {
    font-size: 12px !important;
  }
}

.inline-actions-menu {
  min-width: 160px;
}
</style>
